import { useAtomValue, useSetAtom } from 'jotai';
import { editPointOfInterest as editPointOfInterestAtom } from '../state';
import {
  firstSelectedSphericalIdForVoi,
  viewerCurrentSpherical as viewerCurrentSphericalAtom,
} from '@/components/Analysis/state';
import { PointOfInterestTemplateTypeEnum } from '@/__generated__/graphql';
import { useCallback, useEffect, useMemo } from 'react';

/**
 * Click handler and effect to persist the first spherical ID selected when a VOI creation flow
 *  is initiated from a spherical view. It ensures the original spherical context is preserved even if
 * the user navigates to different sphericals during the creation process to adjust the VOI sizing.
 * Jira: https://abyss-solutions.atlassian.net/browse/WSM-411
 */
export const usePersistFirstVoiSpherical = () => {
  const setFirstSpherical = useSetAtom(firstSelectedSphericalIdForVoi);
  const firstSpherical = useAtomValue(firstSelectedSphericalIdForVoi);
  const editPoi = useAtomValue(editPointOfInterestAtom);
  const viewerCurrentSpherical = useAtomValue(viewerCurrentSphericalAtom);
  const currentCuboidId = viewerCurrentSpherical?.id;

  const isAddingVoi = useMemo(
    () =>
      editPoi.state === 'WaitingForPoint' &&
      editPoi.template?.type === PointOfInterestTemplateTypeEnum.VolumeOfInterest,
    [editPoi.state, editPoi.template?.type]
  );

  const onPointCloudClick = useCallback(() => {
    // Check if adding VOI while inside a spherical
    if (!isAddingVoi || !currentCuboidId) return;
    // If theres already a first spherical id set, do not overwrite it
    // this means that user is navigating to other sphericals to adjust the VOI sizing
    setFirstSpherical((prev) => prev || currentCuboidId);
  }, [isAddingVoi, currentCuboidId, setFirstSpherical]);

  const clearFirstSpherical = useCallback(() => {
    setFirstSpherical(undefined);
  }, [setFirstSpherical]);

  useEffect(() => {
    // If the poi state is not WaitingForDetail or WaitingForTransition, it means that the
    // POI flow has ended, either completed or cancelled, so we clear the first spherical ID
    if (editPoi?.state !== 'WaitingForDetail' && editPoi?.state !== 'WaitingForTransition') {
      setFirstSpherical(undefined);
    }
  }, [editPoi?.state, setFirstSpherical]);

  return { onPointCloudClick, clearFirstSpherical, firstSpherical };
};
